/**
 * Document list filter hook — syncs filter state to URL search params
 */

"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParamsOrEmpty, type ReadonlySearchParams } from "@/hooks/use-search-params-or-empty";
import type { DocumentStatus } from "@/types";

export type SortField = "created_at" | "updated_at" | "original_filename" | "status" | "pushed_at" | "vendor_name" | "invoice_date";
export type SortOrder = "asc" | "desc";
export type QuickFilter = "processing" | "unprocessed" | "has_issues" | null;
export type DateField = "created_at" | "invoice_date";

export interface DocumentFilters {
  status: DocumentStatus | "all";
  search: string;
  schemaId: string;
  vendorId: string;
  source: "" | "email" | "upload";
  tags: string[];
  dateFrom: string;
  dateTo: string;
  dateField: DateField;
  needsReview: boolean;
  inPipeline: boolean;
  showArchived: "" | "only" | "true";
  hasAnomalies: boolean;
  hasErrors: boolean;
  pushReady: boolean | null; // null = no filter
  quickFilter: QuickFilter;
  sortField: SortField;
  sortOrder: SortOrder;
  page: number;
  pageSize: number;
}

/** Page-size options offered in the documents list footer. Max (100) matches the
 *  API route's hard cap in src/app/api/documents/route.ts. */
export const PAGE_SIZE_OPTIONS = [10, 20, 50, 100] as const;
const DEFAULT_PAGE_SIZE = 20;

function parsePageSize(raw: string | null): number {
  const n = parseInt(raw || "", 10);
  return (PAGE_SIZE_OPTIONS as readonly number[]).includes(n) ? n : DEFAULT_PAGE_SIZE;
}

function parseFilters(params: ReadonlySearchParams): DocumentFilters {
  const tagsRaw = params.get("tags");
  return {
    status: (params.get("status") as DocumentStatus | "all") || "matched",
    search: params.get("search") || "",
    schemaId: params.get("schema_id") || "",
    vendorId: params.get("vendor_id") || "",
    source: (params.get("source") as "" | "email" | "upload") || "",
    tags: tagsRaw ? tagsRaw.split(",").filter(Boolean) : [],
    dateFrom: params.get("date_from") || "",
    dateTo: params.get("date_to") || "",
    dateField: params.get("date_field") === "invoice_date" ? "invoice_date" : "created_at",
    needsReview: params.get("needs_review") === "true",
    inPipeline: params.get("in_pipeline") === "true",
    showArchived: (params.get("show_archived") as "" | "only" | "true") || "",
    hasAnomalies: params.get("has_anomalies") === "true",
    hasErrors: params.get("has_errors") === "true",
    pushReady: params.get("push_ready") === "true" ? true : params.get("push_ready") === "false" ? false : null,
    quickFilter: (params.get("quick_filter") as QuickFilter) || null,
    sortField: (params.get("sort") as SortField) || "updated_at",
    sortOrder: (params.get("order") as SortOrder) || "desc",
    page: Math.max(0, parseInt(params.get("page") || "0", 10) || 0),
    pageSize: parsePageSize(params.get("page_size")),
  };
}

function filtersToParams(filters: DocumentFilters): URLSearchParams {
  const params = new URLSearchParams();
  // Persist status in the URL even when "all" so the default ("matched") can be
  // overridden back to "all"; a clean URL now resolves to the "matched" default.
  if (filters.status) params.set("status", filters.status);
  if (filters.search) params.set("search", filters.search);
  if (filters.schemaId) params.set("schema_id", filters.schemaId);
  if (filters.vendorId) params.set("vendor_id", filters.vendorId);
  if (filters.source) params.set("source", filters.source);
  if (filters.tags.length > 0) params.set("tags", filters.tags.join(","));
  if (filters.dateFrom) params.set("date_from", filters.dateFrom);
  if (filters.dateTo) params.set("date_to", filters.dateTo);
  if (filters.dateField === "invoice_date") params.set("date_field", "invoice_date");
  if (filters.needsReview) params.set("needs_review", "true");
  if (filters.inPipeline) params.set("in_pipeline", "true");
  if (filters.showArchived) params.set("show_archived", filters.showArchived);
  if (filters.hasAnomalies) params.set("has_anomalies", "true");
  if (filters.hasErrors) params.set("has_errors", "true");
  if (filters.pushReady !== null) params.set("push_ready", String(filters.pushReady));
  if (filters.quickFilter) params.set("quick_filter", filters.quickFilter);
  if (filters.sortField !== "updated_at") params.set("sort", filters.sortField);
  if (filters.sortOrder !== "desc") params.set("order", filters.sortOrder);
  if (filters.page > 0) params.set("page", String(filters.page));
  if (filters.pageSize !== DEFAULT_PAGE_SIZE) params.set("page_size", String(filters.pageSize));
  return params;
}

export function useDocumentFilters() {
  const searchParams = useSearchParamsOrEmpty();
  const searchTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const pendingSearchRef = useRef<string | null>(null);

  const filters = useMemo(() => parseFilters(searchParams), [searchParams]);
  const paramsKey = searchParams.toString();
  const currentFiltersRef = useRef(filters);
  const [searchValue, setSearchValue] = useState(filters.search);
  const [previousFilterSearch, setPreviousFilterSearch] = useState(filters.search);

  // Keep browser navigation and external URL changes reflected in the input.
  if (previousFilterSearch !== filters.search) {
    setPreviousFilterSearch(filters.search);
    setSearchValue(filters.search);
  }

  const cancelPendingSearch = useCallback(() => {
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
      searchTimerRef.current = undefined;
    }
  }, []);

  // Native history updates integrate with Next.js useSearchParams. Back/Forward
  // emits popstate, so adopt that destination immediately even when an A → B → A
  // traversal lands on the same query string the hook saw previously.
  useEffect(() => {
    const handlePopState = () => {
      cancelPendingSearch();
      pendingSearchRef.current = null;
      const navigatedFilters = parseFilters(new URLSearchParams(window.location.search));
      currentFiltersRef.current = navigatedFilters;
      setSearchValue(navigatedFilters.search);
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [cancelPendingSearch]);

  // Internal replaceState updates already wrote this same snapshot synchronously.
  // A different incoming query therefore came from Link/navigation outside the
  // hook; adopt it and cancel any search that belonged to the previous URL.
  useEffect(() => {
    if (filtersToParams(currentFiltersRef.current).toString() === paramsKey) return;
    cancelPendingSearch();
    pendingSearchRef.current = null;
    currentFiltersRef.current = filters;
    setSearchValue(filters.search);
  }, [cancelPendingSearch, filters, paramsKey]);

  const updateUrl = useCallback(
    (newFilters: DocumentFilters) => {
      const params = filtersToParams(newFilters);
      const qs = params.toString();
      window.history.replaceState(null, "", qs ? `?${qs}` : "?");
    },
    []
  );

  const setFilter = useCallback(
    <K extends keyof DocumentFilters>(key: K, value: DocumentFilters[K]) => {
      const pendingSearch = key === "search" ? null : pendingSearchRef.current;
      cancelPendingSearch();
      pendingSearchRef.current = null;
      const next = { ...currentFiltersRef.current, [key]: value };
      if (pendingSearch !== null) {
        next.search = pendingSearch;
      }
      // Reset page when any non-page filter changes
      if (key !== "page" || pendingSearch !== null) {
        next.page = 0;
      }
      // When setting a quick filter, clear conflicting individual filters
      if (key === "quickFilter" && value) {
        next.status = "all";
        next.needsReview = false;
        next.hasErrors = false;
        next.inPipeline = false;
        next.pushReady = null;
      }
      // When setting an individual filter that overlaps with quick filter, clear quick filter
      if (
        (key === "status" || key === "needsReview" || key === "hasErrors" || key === "inPipeline") &&
        next.quickFilter
      ) {
        next.quickFilter = null;
      }
      currentFiltersRef.current = next;
      setSearchValue(next.search);
      updateUrl(next);
    },
    [cancelPendingSearch, updateUrl]
  );

  const setFilters = useCallback(
    (patch: Partial<DocumentFilters>) => {
      const pendingSearch = "search" in patch ? null : pendingSearchRef.current;
      cancelPendingSearch();
      pendingSearchRef.current = null;
      const next = { ...currentFiltersRef.current, ...patch };
      if (pendingSearch !== null) {
        next.search = pendingSearch;
      }
      if (!("page" in patch) || pendingSearch !== null) {
        next.page = 0;
      }
      currentFiltersRef.current = next;
      setSearchValue(next.search);
      updateUrl(next);
    },
    [cancelPendingSearch, updateUrl]
  );

  const setSearch = useCallback(
    (value: string) => {
      // Debounce search by 300ms
      cancelPendingSearch();
      setSearchValue(value);
      pendingSearchRef.current = value;
      searchTimerRef.current = setTimeout(() => {
        setFilter("search", value);
      }, 300);
    },
    [cancelPendingSearch, setFilter]
  );

  const clearAll = useCallback(() => {
    cancelPendingSearch();
    pendingSearchRef.current = null;
    setSearchValue("");
    const next: DocumentFilters = {
      status: "all",
      search: "",
      schemaId: "",
      vendorId: "",
      source: "",
      tags: [],
      dateFrom: "",
      dateTo: "",
      dateField: "created_at",
      needsReview: false,
      inPipeline: false,
      showArchived: "",
      hasAnomalies: false,
      hasErrors: false,
      pushReady: null,
      quickFilter: null,
      sortField: "updated_at",
      sortOrder: "desc",
      page: 0,
      // Page size is a view preference, not a content filter — preserve it.
      pageSize: currentFiltersRef.current.pageSize,
    };
    currentFiltersRef.current = next;
    updateUrl(next);
  }, [cancelPendingSearch, updateUrl]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.status !== "all") count++;
    if (filters.search) count++;
    if (filters.schemaId) count++;
    if (filters.vendorId) count++;
    if (filters.source) count++;
    if (filters.tags.length > 0) count++;
    if (filters.dateFrom || filters.dateTo) count++;
    if (filters.needsReview) count++;
    if (filters.inPipeline) count++;
    if (filters.showArchived) count++;
    if (filters.hasAnomalies) count++;
    if (filters.hasErrors) count++;
    if (filters.pushReady !== null) count++;
    if (filters.quickFilter) count++;
    return count;
  }, [filters]);

  const toApiParams = useCallback(() => {
    const params = new URLSearchParams();
    params.set("limit", String(filters.pageSize));
    params.set("offset", String(filters.page * filters.pageSize));
    params.set("sort", filters.sortField);
    params.set("order", filters.sortOrder);

    if (filters.status !== "all") params.set("status", filters.status);
    if (filters.search) params.set("search", filters.search);
    if (filters.schemaId) params.set("schema_id", filters.schemaId);
    if (filters.vendorId) params.set("vendor_id", filters.vendorId);
    if (filters.source) params.set("source", filters.source);
    if (filters.tags.length > 0) params.set("tags", filters.tags.join(","));
    if (filters.dateFrom) params.set("date_from", filters.dateFrom);
    if (filters.dateTo) params.set("date_to", filters.dateTo);
    if (filters.dateField === "invoice_date") params.set("date_field", "invoice_date");
    if (filters.needsReview) params.set("needs_review", "true");
    if (filters.inPipeline) params.set("in_pipeline", "true");
    if (filters.showArchived) params.set("show_archived", filters.showArchived);
    if (filters.hasAnomalies) params.set("has_anomalies", "true");
    if (filters.hasErrors) params.set("has_errors", "true");
    if (filters.pushReady !== null) params.set("push_ready", String(filters.pushReady));
    if (filters.quickFilter) params.set("quick_filter", filters.quickFilter);

    return params;
  }, [filters]);

  return {
    filters,
    searchValue,
    setFilter,
    setFilters,
    setSearch,
    clearAll,
    activeFilterCount,
    toApiParams,
    pageSize: filters.pageSize,
    setPageSize: (size: number) => setFilter("pageSize", size),
    pageSizeOptions: PAGE_SIZE_OPTIONS,
  };
}
